import React, { useMemo } from 'react';
import { useAppTheme } from '../../contexts/ThemeContext';

const getStyles = (theme) => ({
  svg: {
    width: '100%',
    height: '100%',
  },
  progressContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.md,
  },
  progressItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.xs,
  },
  progressLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '14px',
    color: theme.colors.text.primary,
  },
  progressBar: {
    width: '100%',
    height: '20px',
    backgroundColor: theme.colors.background.main,
    borderRadius: theme.borderRadius.sm,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: theme.colors.primary,
    transition: 'width 0.3s ease',
    borderRadius: theme.borderRadius.sm,
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.colors.text.muted,
    fontStyle: 'italic',
  },
});

/**
 * Structured Chart Component
 * Renders simple charts and data visualizations
 */
const StructuredChart = ({
  type = 'bar', // 'bar', 'line', 'pie', 'progress'
  data = [],
  title,
  xLabel,
  yLabel,
  width = '100%',
  height = '300px',
  showValues = true,
  showGrid = true,
  className,
  style = {},
}) => {
  const { theme } = useAppTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const chartRef = React.useRef(null);
  const [dimensions, setDimensions] = React.useState({ width: 400, height: 300 });

  React.useEffect(() => {
    if (chartRef.current) {
      const rect = chartRef.current.getBoundingClientRect();
      setDimensions({
        width: rect.width || 400,
        height: parseInt(height) || 300,
      });
    }
  }, [height]);

  const getMaxValue = () => {
    if (type === 'pie') return 100;
    return Math.max(...data.map((item) => (typeof item === 'number' ? item : item.value || 0)));
  };

  const formatValue = (value) => {
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    return value;
  };

  const renderBarChart = () => {
    const maxValue = getMaxValue();
    const chartHeight = dimensions.height - 80;
    const chartWidth = dimensions.width - 80;
    const barWidth = Math.max(20, (chartWidth - 40) / data.length - 10);

    return (
      <svg width={dimensions.width} height={dimensions.height} style={styles.svg}>
        {showGrid &&
          Array.from({ length: 5 }, (_, i) => {
            const y = 40 + (chartHeight * i) / 4;
            return (
              <line
                key={i}
                x1={40}
                y1={y}
                x2={dimensions.width - 20}
                y2={y}
                stroke={theme.colors.border}
                strokeWidth={1}
                opacity={0.3}
              />
            );
          })}

        <line x1={40} y1={40} x2={40} y2={chartHeight + 40} stroke={theme.colors.text.secondary} strokeWidth={2} />

        <line
          x1={40}
          y1={chartHeight + 40}
          x2={dimensions.width - 20}
          y2={chartHeight + 40}
          stroke={theme.colors.text.secondary}
          strokeWidth={2}
        />

        {data.map((item, index) => {
          const value = typeof item === 'number' ? item : item.value || 0;
          const label = typeof item === 'object' ? item.label : `Item ${index + 1}`;
          const barHeight = (value / maxValue) * chartHeight;
          const x = 50 + index * (barWidth + 10);
          const y = chartHeight + 40 - barHeight;

          return (
            <g key={index}>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={typeof item === 'object' && item.color ? item.color : theme.colors.primary}
                opacity={0.8}
              />

              {showValues && (
                <text x={x + barWidth / 2} y={y - 5} textAnchor="middle" fontSize="12" fill={theme.colors.text.primary}>
                  {formatValue(value)}
                </text>
              )}

              <text
                x={x + barWidth / 2}
                y={chartHeight + 55}
                textAnchor="middle"
                fontSize="11"
                fill={theme.colors.text.secondary}
              >
                {label}
              </text>
            </g>
          );
        })}

        {Array.from({ length: 5 }, (_, i) => {
          const value = (maxValue * (4 - i)) / 4;
          const y = 40 + (chartHeight * i) / 4;
          return (
            <text key={i} x={35} y={y + 4} textAnchor="end" fontSize="11" fill={theme.colors.text.secondary}>
              {formatValue(Math.round(value))}
            </text>
          );
        })}
      </svg>
    );
  };

  const renderPieChart = () => {
    const total = data.reduce((sum, item) => sum + (typeof item === 'number' ? item : item.value || 0), 0);
    const centerX = dimensions.width / 2;
    const centerY = dimensions.height / 2;
    const radius = Math.min(centerX, centerY) - 40;

    let currentAngle = -Math.PI / 2;

    return (
      <svg width={dimensions.width} height={dimensions.height} style={styles.svg}>
        {data.map((item, index) => {
          const value = typeof item === 'number' ? item : item.value || 0;
          const label = typeof item === 'object' ? item.label : `Item ${index + 1}`;
          const percentage = (value / total) * 100;
          const angle = (value / total) * 2 * Math.PI;

          const x1 = centerX + radius * Math.cos(currentAngle);
          const y1 = centerY + radius * Math.sin(currentAngle);

          const x2 = centerX + radius * Math.cos(currentAngle + angle);
          const y2 = centerY + radius * Math.sin(currentAngle + angle);

          const largeArcFlag = angle > Math.PI ? 1 : 0;

          const pathData = [
            `M ${centerX} ${centerY}`,
            `L ${x1} ${y1}`,
            `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
            'Z',
          ].join(' ');

          const color =
            typeof item === 'object' && item.color ? item.color : `hsl(${(index * 360) / data.length}, 70%, 50%)`;

          const labelAngle = currentAngle + angle / 2;
          const labelX = centerX + radius * 0.7 * Math.cos(labelAngle);
          const labelY = centerY + radius * 0.7 * Math.sin(labelAngle);

          currentAngle += angle;

          return (
            <g key={index}>
              <path d={pathData} fill={color} opacity={0.8} stroke={theme.colors.background.main} strokeWidth={2} />

              {showValues && percentage > 5 && (
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor="middle"
                  fontSize="11"
                  fill={theme.colors.text.inverse}
                  fontWeight="600"
                >
                  {percentage.toFixed(1)}%
                </text>
              )}
            </g>
          );
        })}
      </svg>
    );
  };

  const renderProgressChart = () => {
    return (
      <div style={styles.progressContainer}>
        {data.map((item, index) => {
          const value = typeof item === 'number' ? item : item.value || 0;
          const label = typeof item === 'object' ? item.label : `Item ${index + 1}`;
          const max = typeof item === 'object' ? item.max || 100 : 100;
          const percentage = Math.min(100, (value / max) * 100);

          return (
            <div key={index} style={styles.progressItem}>
              <div style={styles.progressLabel}>
                <span>{label}</span>
                <span>
                  {formatValue(value)}
                  {typeof item === 'object' && item.unit ? item.unit : ''}
                </span>
              </div>
              <div style={styles.progressBar}>
                <div
                  style={{
                    ...styles.progressFill,
                    width: `${percentage}%`,
                    backgroundColor: typeof item === 'object' && item.color ? item.color : theme.colors.primary,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderChart = () => {
    switch (type) {
      case 'pie':
        return renderPieChart();
      case 'progress':
        return renderProgressChart();
      case 'bar':
      default:
        return renderBarChart();
    }
  };

  const containerStyle = {
    backgroundColor: theme.colors.background.sidebar,
    borderRadius: theme.borderRadius.md,
    border: `1px solid ${theme.colors.border}`,
    padding: theme.spacing.md,
    width,
    height,
    ...style,
  };

  const headerStyle = {
    marginBottom: theme.spacing.md,
    textAlign: 'center',
  };

  const titleStyle = {
    fontSize: '16px',
    fontWeight: '600',
    color: theme.colors.text.primary,
    margin: 0,
  };

  const chartContainerStyle = {
    width: '100%',
    height: type === 'progress' ? 'auto' : `calc(100% - ${title ? '60px' : '20px'})`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (!data || data.length === 0) {
    return (
      <div style={containerStyle} className={className}>
        {title && (
          <div style={headerStyle}>
            <h3 style={titleStyle}>{title}</h3>
          </div>
        )}
        <div style={styles.emptyState}>No data to display</div>
      </div>
    );
  }

  return (
    <div style={containerStyle} className={className}>
      {title && (
        <div style={headerStyle}>
          <h3 style={titleStyle}>{title}</h3>
        </div>
      )}

      <div ref={chartRef} style={chartContainerStyle}>
        {renderChart()}
      </div>
    </div>
  );
};

export default StructuredChart;
